import { useMemo } from "react"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef } from "material-react-table"
import { Button, Grid, Skeleton, Typography } from "@mui/material"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import {
    FetchDetailedPriceListsResponse,
    FetchPriceListsResponse,
    PriceListQuery,
    PriceListType,
    fetchDetailedPriceLists,
    fetchPriceLists,
} from "../../../priceList/priceListApi"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../../merchants/merchantHooks"

const useStyles = makeStyles()((theme) => ({
    tableHeadRow: {
        boxShadow: "none",
        background: theme.palette.tailwind.slate[50],
    },
    singleCell: {
        verticalAlign: "baseline",
    },
    hiddenHeader: {
        "& >div": {
            display: "none",
        },
    },
    button: {
        color: theme.palette.common.white,
        backgroundColor: "#0F172A",
        textTransform: "none",
        "&:hover": {
            backgroundColor: "#0F172A",
        },
    },
}))

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists || []

type PriceListsProps = Readonly<{
    handleSelectPriceList: (id: number) => void
}>

export function PriceListTable({ handleSelectPriceList }: PriceListsProps) {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()

    const priceLists = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId: merchantId,
        },
        select: selectPriceLists,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(priceLists)

    const columns = useMemo<MRT_ColumnDef<PriceListType>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("product-create-price-list-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("product-create-price-list-table-status-column"),
                },
                (originalRow) => originalRow.isActive,
            ),
            {
                header: "Select",
                Cell: ({ row }) => {
                    return (
                        <LocalizedStrict id="product-create-price-list-table-select-button">
                            <Button
                                onClick={() => handleSelectPriceList(row.original.id)}
                                variant="contained"
                                size="small"
                                className={classes.button}
                            >
                                Select
                            </Button>
                        </LocalizedStrict>
                    )
                },
                muiTableHeadCellProps: {
                    align: "center",
                    className: classes.hiddenHeader,
                },
                muiTableBodyCellProps: {
                    align: "center",
                    className: classes.singleCell,
                },
            },
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    defaultColumn={{
                        minSize: 5, //allow columns to get smaller than default
                        maxSize: 5, //allow columns to get larger than default
                        size: 5, //make columns wider by default
                    }}
                    enableClickToCopy={false}
                    enableColumnOrdering
                    enableStickyHeader
                    enableDensityToggle={false}
                    initialState={{ density: "spacious" }}
                    enableHiding={false}
                    enableRowActions={false}
                    enableGrouping
                    muiTableContainerProps={{ sx: { maxHeight: "100%" } }}
                    muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                    positionActionsColumn="last"
                    enableColumnDragging={false}
                    enableExpandAll={false}
                    enableTopToolbar={false}
                    renderDetailPanel={({ row }) => {
                        return <PriceListDetails id={row.original.id} />
                    }}
                />
            </Grid>
        </>
    )
}

type DetailedPriceListProps = Readonly<{
    id: number
}>

const useStylesDetails = makeStyles()((theme) => ({
    tableDetailsContainer: {
        paddingLeft: "1.25rem",
        // borderLeft: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderLeft: `1px solid #E2E8F0`,
        marginLeft: "10%",
        marginBottom: "1rem",
    },
}))

const selectPriceListDetails = (data: FetchDetailedPriceListsResponse) => data.priceList

const PriceListDetails = ({ id }: DetailedPriceListProps) => {
    const { classes } = useStylesDetails()
    const detailedPriceList = useApiQuery(fetchDetailedPriceLists, {
        queryName: PriceListQuery.DETAILS,
        dependencies: {
            id: id ?? 0,
        },
        select: selectPriceListDetails,
        enabled: Boolean(id),
    })

    if (detailedPriceList.data) {
        const { priceSetup, shops, customers } = detailedPriceList.data
        const percentage =
            (priceSetup?.vatCode?.vatPercentage.amount || 0) /
            Math.pow(10, priceSetup?.vatCode?.vatPercentage.scale || 0)
        const shopNames = shops.map((shop) => shop.name).join(", ")
        const customersNames = customers.map((customer) => customer.name).join(", ")
        return (
            <>
                <Grid container flexDirection="column" gap={3} className={classes.tableDetailsContainer}>
                    <Typography variant="caption">
                        <LocalizedStrict id="product-create-price-list-table-currency">
                            <Typography variant="inherit" fontWeight={600}>
                                Currency
                            </Typography>
                        </LocalizedStrict>
                        {priceSetup?.currencyCode ?? "-"}
                    </Typography>
                    <Typography variant="caption">
                        <LocalizedStrict id="product-create-price-list-table-vat">
                            <Typography variant="inherit" fontWeight={600}>
                                VAT
                            </Typography>
                        </LocalizedStrict>
                        {priceSetup?.vatCode?.name} {`${percentage}%`}
                    </Typography>
                    <Typography variant="caption">
                        <LocalizedStrict id="product-create-price-list-table-connected-shops">
                            <Typography variant="inherit" fontWeight={600}>
                                Connected shops:
                            </Typography>
                        </LocalizedStrict>
                        {shopNames}
                    </Typography>
                    <Typography variant="caption">
                        <LocalizedStrict id="product-create-price-list-table-connected-customers">
                            <Typography variant="inherit" fontWeight={600}>
                                Connected customers:
                            </Typography>
                        </LocalizedStrict>
                        {customersNames}
                    </Typography>
                </Grid>
            </>
        )
    }

    return <Skeleton variant="rectangular" width="100%" height={250} />
}
